import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, ArrowLeft } from "lucide-react";

function EditSupplier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    supplier_category_id: "",
    nit: "",
    address: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargamos ambas cosas al tiempo
        const [supplierRes, categoriesRes] = await Promise.all([
          api.get(`/suppliers/${id}`),
          api.get("/supplier-categories")
        ]);

        setFormData(supplierRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        toast.error("Error al cargar datos");
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "El nombre es obligatorio";
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Correo inválido";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // ✅ NORMALIZAR
    const payload = {
      ...formData,
      nit: formData.nit?.trim() === "" ? null : formData.nit,
      email: formData.email?.trim() === "" ? null : formData.email,
    };

    try {
      await api.put(`/suppliers/${id}`, payload);
      toast.success("Proveedor actualizado correctamente");
      setErrors({});
      navigate("/suppliers");
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.errors) {
        let backendErrors = {};
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path] = err.msg;
        });
        setErrors(backendErrors);
        return;
      }

      setErrors({ general: "Ocurrió un error inesperado" });
      toast.error("Error al actualizar el proveedor");
    }
  };



  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
      <button
        onClick={() => navigate("/suppliers")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a proveedores
      </button>

      <h2 className="text-3xl font-semibold text-gray-800 mb-6">
        Editar Proveedor
      </h2>

      {errors.general && (
        <div className="text-red-600 mb-4 text-sm">{errors.general}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre
          </label>
          <input
            type="text"
            name="name"
            value={formData.name || ""}
            onChange={handleChange}
            placeholder="Nombre del proveedor"
            className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.name ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-yellow-500"
              }`}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* 🟢 SELECT DE CATEGORÍA EN EDICIÓN */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Categoría de Proveedor
          </label>
          <select
            name="supplier_category_id"
            value={formData.supplier_category_id || ""}
            onChange={handleChange}
            className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.supplier_category_id ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-yellow-500"
              }`}
          >
            <option value="">Seleccione una categoría...</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.supplier_category_id && (
            <p className="text-red-500 text-sm mt-1">{errors.supplier_category_id}</p>
          )}
        </div>

        {/* NIT */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            NIT
          </label>
          <input
            type="text"
            name="nit"
            value={formData.nit || ""}
            onChange={handleChange}
            placeholder="NIT del proveedor"
            className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.nit
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-yellow-500"
              }`}
          />
          {errors.nit && (
            <p className="text-red-500 text-sm mt-1">{errors.nit}</p>
          )}
        </div>


        {/* Dirección */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dirección
          </label>
          <input
            type="text"
            name="address"
            value={formData.address || ""}
            onChange={handleChange}
            placeholder="Dirección del proveedor"
            className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.address ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-yellow-500"
              }`}
          />
          {errors.address && (
            <p className="text-red-500 text-sm mt-1">{errors.address}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone || ""}
              onChange={handleChange}
              placeholder="Ej: 3001234567"
              className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.phone ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-yellow-500"
                }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correo electrónico
            </label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors.email ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-yellow-500"
                }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-lg shadow transition duration-300"
        >
          <Save size={18} />
          Guardar Cambios
        </button>
      </form>
    </div>
  );
}

export default EditSupplier;
